import { zodResolver } from '@hookform/resolvers/zod';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import LockOutlined from '@mui/icons-material/LockOutlined';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import PhonelinkLockRounded from '@mui/icons-material/PhonelinkLockRounded';
import { Alert, Box, Button, InputAdornment, Link, Stack, TextField } from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import { z } from 'zod';
import { ApiError } from '../api/client';
import { AuthLayout } from './AuthLayout';
import { useAuth } from './AuthProvider';
import { PasswordField } from './PasswordField';
import { loginErrorMessage, mfaErrorMessage, normalizeRecoveryCode } from './messages';
import { authButtonSx } from './styles';

const schema = z.object({
  email: z.string().trim().min(1, 'Enter your email').pipe(z.email('Enter a valid email address')),
  password: z.string().min(1, 'Enter your password'),
});
type Values = z.infer<typeof schema>;

/** Two-step sign-in: email + password, then (when MFA is on) an authenticator or recovery code. */
export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<{ mfaToken: string; email: string } | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const done = () => {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    void navigate(from === '/login' ? '/' : from, { replace: true });
  };

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    try {
      const next = await login(values.email.trim(), values.password);
      if (next) setChallenge({ mfaToken: next.mfaToken, email: values.email.trim() });
      else done();
    } catch (e) {
      setError(loginErrorMessage(e));
    }
  });

  if (challenge)
    return (
      <MfaStep
        mfaToken={challenge.mfaToken}
        email={challenge.email}
        onDone={done}
        onRestart={(message) => {
          setChallenge(null);
          setError(message ?? null);
        }}
      />
    );

  const icon = (Icon: typeof LockOutlined) => (
    <InputAdornment position="start">
      <Icon sx={{ fontSize: 20, color: 'text.secondary' }} />
    </InputAdornment>
  );

  return (
    <AuthLayout
      title="Platform sign-in"
      subtitle="Sign in with your platform staff account to manage tenants and subscriptions."
    >
      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            size="medium"
            label="Work email"
            type="email"
            autoComplete="username"
            autoFocus
            {...register('email')}
            error={Boolean(errors.email)}
            helperText={errors.email?.message}
            slotProps={{ input: { startAdornment: icon(MailOutlineRounded) } }}
          />
          <PasswordField
            label="Password"
            autoComplete="current-password"
            registration={register('password')}
            error={errors.password?.message}
            startIcon={icon(LockOutlined)}
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            loading={isSubmitting}
            fullWidth
            endIcon={<ArrowForwardRounded />}
            sx={authButtonSx}
          >
            Sign in
          </Button>
        </Stack>
      </form>
    </AuthLayout>
  );
}

function MfaStep({
  mfaToken,
  email,
  onDone,
  onRestart,
}: {
  mfaToken: string;
  email: string;
  onDone: () => void;
  onRestart: (message?: string) => void;
}) {
  const { verifyMfa } = useAuth();
  const [recovery, setRecovery] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const normalized = recovery ? normalizeRecoveryCode(code) : code.replace(/\D/g, '');
  const valid = recovery ? /^[a-z0-9]{4}-[a-z0-9]{4}$/.test(normalized) : normalized.length === 6;

  const submit = async (value = normalized) => {
    setError(null);
    setBusy(true);
    try {
      await verifyMfa(mfaToken, value);
      onDone();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'MFA_TOKEN_INVALID') {
        onRestart(mfaErrorMessage(e));
        return;
      }
      setError(mfaErrorMessage(e));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Two-step verification"
      subtitle={
        recovery
          ? 'Enter one of the recovery codes you saved when you set up two-step verification. Each code works once.'
          : `Open your authenticator app and enter the 6-digit code for ${email}.`
      }
      icon={
        <Box
          sx={(t) => {
            const p = t.vars?.palette.primary.main ?? t.palette.primary.main;
            return {
              width: 52,
              height: 52,
              borderRadius: '16px',
              display: 'grid',
              placeItems: 'center',
              color: 'primary.main',
              bgcolor: `color-mix(in srgb, ${p} 10%, transparent)`,
              border: `1px solid color-mix(in srgb, ${p} 20%, transparent)`,
            };
          }}
        >
          <PhonelinkLockRounded />
        </Box>
      }
      footer={
        <Stack spacing={1} sx={{ alignItems: 'center' }}>
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            onClick={() => {
              setRecovery((r) => !r);
              setCode('');
              setError(null);
            }}
            sx={{ fontWeight: 600 }}
          >
            {recovery ? 'Use the code from my authenticator app' : 'Use a recovery code instead'}
          </Link>
          <Button
            variant="text"
            size="small"
            startIcon={<ArrowBackRounded />}
            onClick={() => onRestart()}
            sx={{ color: 'text.secondary' }}
          >
            Back to sign-in
          </Button>
        </Stack>
      }
    >
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (valid && !busy) void submit();
        }}
      >
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}
          {recovery ? (
            <TextField
              key="recovery"
              size="medium"
              label="Recovery code"
              placeholder="xxxx-xxxx"
              autoFocus
              autoComplete="off"
              value={code}
              onChange={(e) => setCode(e.target.value.slice(0, 12))}
              slotProps={{
                htmlInput: {
                  spellCheck: false,
                  style: { fontFamily: 'ui-monospace, Menlo, monospace', letterSpacing: '0.12em' },
                },
              }}
            />
          ) : (
            <TextField
              key="totp"
              size="medium"
              label="6-digit code"
              placeholder="000000"
              autoFocus
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                setCode(digits);
                // Submit as soon as the sixth digit arrives (also on paste).
                if (digits.length === 6 && !busy) void submit(digits);
              }}
              slotProps={{
                htmlInput: {
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  maxLength: 6,
                  style: {
                    fontSize: 24,
                    letterSpacing: '0.5em',
                    textAlign: 'center',
                    fontVariantNumeric: 'tabular-nums',
                  },
                },
              }}
            />
          )}
          <Button
            type="submit"
            variant="contained"
            size="large"
            loading={busy}
            disabled={!valid}
            fullWidth
            endIcon={<ArrowForwardRounded />}
            sx={authButtonSx}
          >
            Verify and sign in
          </Button>
        </Stack>
      </form>
    </AuthLayout>
  );
}
